import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

interface UseScrollBarProps {
  listContainerRef: React.RefObject<HTMLDivElement>
  setScrollbarWidth: React.Dispatch<React.SetStateAction<number>>
  screens?: ListScreensByTagsResponse[]
  selectedScreens: ListScreensByTagsResponse[] | null
}

export function useScrollbar({
  listContainerRef,
  setScrollbarWidth,
  screens,
  selectedScreens
}: UseScrollBarProps) {
  const scrollPositionRef = useRef<number>(0)
  const shouldRestoreScroll = useRef<boolean>(false)

  // Detect if the container has a scrollbar and calculate its width
  const checkScrollbar = useCallback(() => {
    if (!listContainerRef?.current) return

    const container = listContainerRef?.current
    const hasVerticalScroll = container?.scrollHeight > container?.clientHeight

    if (hasVerticalScroll) {
      const outer = document.createElement('div')
      outer.style.visibility = 'hidden'
      outer.style.overflow = 'scroll'
      outer.style.msOverflowStyle = 'scrollbar'
      document.body.appendChild(outer)

      const inner = document.createElement('div')
      outer.appendChild(inner)

      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth
      document.body.removeChild(outer)

      setScrollbarWidth(scrollbarWidth)
    } else {
      setScrollbarWidth(0)
    }
  }, [])

  // Observe changes in the content to detect when the scroll appears/disappears
  useEffect(() => {
    if (!listContainerRef?.current) return

    const resizeObserver = new ResizeObserver(() => {
      checkScrollbar()
    })

    const mutationObserver = new MutationObserver(() => {
      checkScrollbar()
    })

    const container = listContainerRef?.current
    resizeObserver?.observe(container)
    mutationObserver?.observe(container, { childList: true, subtree: true })

    setTimeout(checkScrollbar, 0)

    return () => {
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
    }
  }, [checkScrollbar, screens, listContainerRef?.current])

  useLayoutEffect(() => {
    if (shouldRestoreScroll?.current && listContainerRef?.current) {
      listContainerRef.current.scrollTop = scrollPositionRef?.current
      shouldRestoreScroll.current = false
    }
  }, [selectedScreens])

  const getCurrentScrollPosition = useCallback(() => {
    if (listContainerRef?.current) {
      scrollPositionRef.current = listContainerRef?.current?.scrollTop
      shouldRestoreScroll.current = true
    }
  }, [listContainerRef?.current])

  return { getCurrentScrollPosition }
}
